import { Alert, Box, Button, Container, Paper, Stack, TextField, Typography } from '@mui/material';
import { useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../core/contexts/AuthContext';
import { extractErrorMessage } from '../../core/services/api';

/** Handoff from LoginPage/RegistrationPage after an unrecognized Google sign-in. */
export interface CompleteSignupState {
  signupToken: string;
  email: string;
  name: string | null;
  /** Where to go once the workspace exists — the page that sent the user to sign in, if any. */
  from?: string;
}

/**
 * Second step of Google signup: the account doesn't exist yet, so we ask for
 * a workspace name before creating the user and tenant. The verified identity
 * travels in the signup token, not in this form.
 */
export function CompleteSignupPage() {
  const { isAuthenticated, completeGoogleSignup, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state as CompleteSignupState | null) ?? null;
  const [tenantName, setTenantName] = useState('');
  const [error, setError] = useState<string | null>(null);

  if (isAuthenticated) return <Navigate to="/" replace />;
  // Reached directly, without a Google sign-in behind it.
  if (!state?.signupToken) return <Navigate to="/login" replace />;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await completeGoogleSignup(state.signupToken, tenantName);
      // A brand-new account has no projects yet, so the onboarding picker is a
      // better first screen than an empty dashboard.
      navigate(state.from ?? '/onboarding', { replace: true });
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        backgroundColor: 'background.default',
      }}
    >
      <Container maxWidth="xs">
        <Paper sx={{ p: 4 }}>
          <Stack spacing={1} sx={{ mb: 3, textAlign: 'center' }}>
            <Typography variant="h5" color="primary" fontWeight={700}>
              Name your workspace
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Signed in as {state.email}. One last step before you get started.
            </Typography>
          </Stack>

          <form onSubmit={onSubmit} noValidate>
            <Stack spacing={2}>
              {error && <Alert severity="error">{error}</Alert>}
              <TextField
                label="Workspace name"
                fullWidth
                required
                autoFocus
                value={tenantName}
                onChange={(e) => setTenantName(e.target.value)}
                helperText="Displayed in the tenant switcher. You can rename it later."
              />
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={loading || !tenantName.trim()}
              >
                {loading ? 'Creating workspace…' : 'Create workspace'}
              </Button>
            </Stack>
          </form>
        </Paper>
      </Container>
    </Box>
  );
}
